import { supabaseService } from '../db/supabase';
import { UIComponents } from '../ui/components';
import type { AppState, Campus, Sede, Bloco, Andar, Sala } from '../types';

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

export class ModuloLocais {
  private campi: Campus[] = [];
  private sedes: Sede[] = [];
  private blocos: Bloco[] = [];
  private andares: Andar[] = [];
  private salas: Sala[] = [];
  private expandedItems: Set<string> = new Set();

  constructor(_state: AppState) {}

  async inicializar() {
    await this.carregarDados();
    this.renderizarArvore();
    this.vincularEventos();
  }

  private async carregarDados() {
    try {
      const [campiData, sedesData, blocosData, andaresData, salasData] = await Promise.all([
        supabaseService.buscarTodosCampus(),
        supabaseService.buscarSedesPorCampus(''),
        supabaseService.buscarBlocosPorSede(''),
        supabaseService.buscarAndaresPorBloco(''),
        supabaseService.buscarSalasPorAndar('')
      ]);

      this.campi = campiData;
      this.sedes = sedesData;
      this.blocos = blocosData;
      this.andares = andaresData;
      this.salas = salasData;
    } catch (error) {
      console.error('Erro ao carregar locais:', error);
      UIComponents.error('Erro ao carregar estrutura de locais.');
    }
  }

  private renderizarArvore() {
    const container = byId('locais-arvore');
    if (!container) return;

    container.innerHTML = '';

    if (this.campi.length === 0) {
      container.innerHTML = '<p class="text-muted">Nenhum campus cadastrado.</p>';
      return;
    }

    this.campi.forEach(campus => {
      const campusNode = this.criarNoCampus(campus);
      container.appendChild(campusNode);
    });
  }

  private criarNoCampus(campus: Campus): HTMLElement {
    const div = document.createElement('div');
    div.className = 'locais-item campus-item';

    const sedesDoCampus = this.sedes.filter(s => s.campus_sigla === campus.sigla && s.ativo);
    const isExpanded = this.expandedItems.has(`campus-${campus.sigla}`);
    const hasChildren = sedesDoCampus.length > 0;

    div.innerHTML = `
      <div class="locais-row campus-row">
        <div class="locais-info">
          ${hasChildren ? `<button class="btn-toggle" data-id="campus-${campus.sigla}" title="${isExpanded ? 'Recolher' : 'Expandir'}">
            <span class="material-icons-round">${isExpanded ? 'expand_more' : 'chevron_right'}</span>
          </button>` : ''}
          <span class="locais-icon">🏛️</span>
          <span class="locais-nome">${campus.nome}</span>
          <span class="locais-sigla">(${campus.sigla})</span>
          <span class="locais-cidade">${campus.cidade}</span>
        </div>
      </div>
    `;

    if (hasChildren) {
      const sedesContainer = document.createElement('div');
      sedesContainer.className = `locais-children ${isExpanded ? '' : 'hidden'}`;

      sedesDoCampus.forEach(sede => {
        const sedeNode = this.criarNoSede(sede);
        sedesContainer.appendChild(sedeNode);
      });

      div.appendChild(sedesContainer);
    }

    return div;
  }

  private criarNoSede(sede: Sede): HTMLElement {
    const div = document.createElement('div');
    div.className = 'locais-item sede-item';

    const blocosDaSede = this.blocos.filter(b => b.sede_id === sede.id && b.ativo);
    const isExpanded = this.expandedItems.has(`sede-${sede.id}`);
    const hasChildren = blocosDaSede.length > 0;

    div.innerHTML = `
      <div class="locais-row sede-row">
        <div class="locais-info">
          ${hasChildren ? `<button class="btn-toggle" data-id="sede-${sede.id}" title="${isExpanded ? 'Recolher' : 'Expandir'}">
            <span class="material-icons-round">${isExpanded ? 'expand_more' : 'chevron_right'}</span>
          </button>` : ''}
          <span class="locais-icon">🏢</span>
          <span class="locais-nome">${sede.nome}</span>
          <span class="locais-sigla">(${sede.sigla})</span>
        </div>
        <div class="locais-actions">
          <button class="btn-icon btn-edit" data-type="sede" data-id="${sede.id}" title="Editar">
            <span class="material-icons-round">edit</span>
          </button>
          <button class="btn-icon btn-inactivate" data-type="sede" data-id="${sede.id}" title="Inativar">
            <span class="material-icons-round">block</span>
          </button>
        </div>
      </div>
    `;

    if (hasChildren) {
      const blocosContainer = document.createElement('div');
      blocosContainer.className = `locais-children ${isExpanded ? '' : 'hidden'}`;

      blocosDaSede.forEach(bloco => {
        const blocoNode = this.criarNoBloco(bloco);
        blocosContainer.appendChild(blocoNode);
      });

      div.appendChild(blocosContainer);
    }

    return div;
  }

  private criarNoBloco(bloco: Bloco): HTMLElement {
    const div = document.createElement('div');
    div.className = 'locais-item bloco-item';

    const andaresDoBloco = this.andares.filter(a => a.bloco_id === bloco.id && a.ativo);
    const isExpanded = this.expandedItems.has(`bloco-${bloco.id}`);
    const hasChildren = andaresDoBloco.length > 0;

    div.innerHTML = `
      <div class="locais-row bloco-row">
        <div class="locais-info">
          ${hasChildren ? `<button class="btn-toggle" data-id="bloco-${bloco.id}" title="${isExpanded ? 'Recolher' : 'Expandir'}">
            <span class="material-icons-round">${isExpanded ? 'expand_more' : 'chevron_right'}</span>
          </button>` : ''}
          <span class="locais-icon">🏗️</span>
          <span class="locais-nome">${bloco.nome}</span>
          <span class="locais-sigla">(${bloco.sigla})</span>
          ${bloco.descricao ? `<span class="locais-desc">${bloco.descricao}</span>` : ''}
        </div>
        <div class="locais-actions">
          <button class="btn-icon btn-edit" data-type="bloco" data-id="${bloco.id}" title="Editar">
            <span class="material-icons-round">edit</span>
          </button>
          <button class="btn-icon btn-inactivate" data-type="bloco" data-id="${bloco.id}" title="Inativar">
            <span class="material-icons-round">block</span>
          </button>
        </div>
      </div>
    `;

    if (hasChildren) {
      const andaresContainer = document.createElement('div');
      andaresContainer.className = `locais-children ${isExpanded ? '' : 'hidden'}`;

      andaresDoBloco.forEach(andar => {
        const andarNode = this.criarNoAndar(andar);
        andaresContainer.appendChild(andarNode);
      });

      div.appendChild(andaresContainer);
    }

    return div;
  }

  private criarNoAndar(andar: Andar): HTMLElement {
    const div = document.createElement('div');
    div.className = 'locais-item andar-item';

    const salasDoAndar = this.salas.filter(s => s.andar_id === andar.id && s.ativo);
    const isExpanded = this.expandedItems.has(`andar-${andar.id}`);
    const hasChildren = salasDoAndar.length > 0;

    div.innerHTML = `
      <div class="locais-row andar-row">
        <div class="locais-info">
          ${hasChildren ? `<button class="btn-toggle" data-id="andar-${andar.id}" title="${isExpanded ? 'Recolher' : 'Expandir'}">
            <span class="material-icons-round">${isExpanded ? 'expand_more' : 'chevron_right'}</span>
          </button>` : ''}
          <span class="locais-icon">📍</span>
          <span class="locais-nome">${andar.nome}</span>
          ${andar.descricao ? `<span class="locais-desc">${andar.descricao}</span>` : ''}
        </div>
        <div class="locais-actions">
          <button class="btn-icon btn-edit" data-type="andar" data-id="${andar.id}" title="Editar">
            <span class="material-icons-round">edit</span>
          </button>
          <button class="btn-icon btn-inactivate" data-type="andar" data-id="${andar.id}" title="Inativar">
            <span class="material-icons-round">block</span>
          </button>
        </div>
      </div>
    `;

    if (hasChildren) {
      const salasContainer = document.createElement('div');
      salasContainer.className = `locais-children ${isExpanded ? '' : 'hidden'}`;

      salasDoAndar.forEach(sala => {
        const salaNode = this.criarNoSala(sala);
        salasContainer.appendChild(salaNode);
      });

      div.appendChild(salasContainer);
    }

    return div;
  }

  private criarNoSala(sala: Sala): HTMLElement {
    const div = document.createElement('div');
    div.className = 'locais-item sala-item';

    div.innerHTML = `
      <div class="locais-row sala-row">
        <div class="locais-info">
          <span class="locais-icon">🚪</span>
          <span class="locais-nome">${sala.nome}</span>
          ${sala.descricao ? `<span class="locais-desc">${sala.descricao}</span>` : ''}
          ${sala.setor ? `<span class="locais-setor">${sala.setor}</span>` : ''}
        </div>
        <div class="locais-actions">
          <button class="btn-icon btn-edit" data-type="sala" data-id="${sala.id}" title="Editar">
            <span class="material-icons-round">edit</span>
          </button>
          <button class="btn-icon btn-inactivate" data-type="sala" data-id="${sala.id}" title="Inativar">
            <span class="material-icons-round">block</span>
          </button>
        </div>
      </div>
    `;

    return div;
  }

  private vincularEventos() {
    const btnAdicionar = byId('locais-btn-adicionar');
    const addMenu = byId('locais-add-menu');
    
    if (btnAdicionar && addMenu) {
      btnAdicionar.addEventListener('click', (e) => {
        e.stopPropagation();
        addMenu.classList.toggle('hidden');
      });

      // Fechar menu ao clicar fora
      document.addEventListener('click', (e) => {
        if (!addMenu.contains(e.target as HTMLElement) && !btnAdicionar.contains(e.target as HTMLElement)) {
          addMenu.classList.add('hidden');
        }
      });

      // Opções do menu
      const addOptions = addMenu.querySelectorAll('.locais-add-option');
      addOptions.forEach(option => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          const tipo = (e.currentTarget as HTMLElement).dataset.type;
          if (tipo) {
            addMenu.classList.add('hidden');
            this.abrirModalAdicionar(tipo);
          }
        });
      });
    }

    const btnFechar = byId('locais-modal-fechar');
    const btnCancelar = byId('locais-modal-cancelar');
    if (btnFechar) btnFechar.addEventListener('click', () => this.fecharModal());
    if (btnCancelar) btnCancelar.addEventListener('click', () => this.fecharModal());

    const form = byId('locais-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.salvar();
      });
    }

    const selectTipo = byId<HTMLSelectElement>('locais-tipo');
    if (selectTipo) {
      selectTipo.addEventListener('change', () => this.atualizarSelects());
    }

    // Atualizar selects dependentes quando o pai mudar
    const selectCampus = byId<HTMLSelectElement>('locais-campus');
    const selectSede = byId<HTMLSelectElement>('locais-sede');
    const selectBloco = byId<HTMLSelectElement>('locais-bloco');
    const selectAndar = byId<HTMLSelectElement>('locais-andar');

    if (selectCampus) {
      selectCampus.addEventListener('change', () => {
        this.atualizarSelectSedes();
        if (selectSede) selectSede.value = '';
        if (selectBloco) selectBloco.value = '';
        if (selectAndar) selectAndar.value = '';
      });
    }

    if (selectSede) {
      selectSede.addEventListener('change', () => {
        this.atualizarSelectBlocos();
        if (selectBloco) selectBloco.value = '';
        if (selectAndar) selectAndar.value = '';
      });
    }

    if (selectBloco) {
      selectBloco.addEventListener('change', () => {
        this.atualizarSelectAndares();
        if (selectAndar) selectAndar.value = '';
      });
    }

    const container = byId('locais-arvore');
    if (container) {
      container.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        
        // Toggle expand/collapse
        const toggleBtn = target.closest('.btn-toggle') as HTMLElement;
        if (toggleBtn) {
          const itemId = toggleBtn.dataset.id;
          if (itemId) {
            if (this.expandedItems.has(itemId)) {
              this.expandedItems.delete(itemId);
            } else {
              this.expandedItems.add(itemId);
            }
            this.renderizarArvore();
            return;
          }
        }

        // Action buttons (edit/inactivate)
        const btn = target.closest('.btn-icon') as HTMLElement;
        if (btn) {
          const type = btn.dataset.type;
          const id = btn.dataset.id;
          const action = btn.classList.contains('btn-edit') ? 'edit' : 'inactivate';
          
          if (type && id) {
            if (action === 'edit') {
              this.abrirModalEditar(type, id);
            } else {
              this.inativarItem(type, id);
            }
          }
        }
      });
    }
  }

  private abrirModalAdicionar(tipo: string = 'sede') {
    const modal = byId<HTMLDialogElement>('locais-modal');
    const selectTipo = byId<HTMLSelectElement>('locais-tipo');
    const editId = byId<HTMLInputElement>('locais-edit-id');
    const editType = byId<HTMLInputElement>('locais-edit-type');
    const modalTitle = byId('locais-modal-title');

    if (!modal || !selectTipo) return;

    editId!.value = '';
    editType!.value = '';
    modalTitle!.textContent = `Adicionar ${tipo.charAt(0).toUpperCase() + tipo.slice(1)}`;
    selectTipo.value = tipo;
    selectTipo.disabled = true;
    
    this.limparForm();
    this.atualizarSelects();
    modal.showModal();
  }

  private limparForm() {
    const inputNome = byId<HTMLInputElement>('locais-nome');
    const inputSigla = byId<HTMLInputElement>('locais-sigla');
    const inputDescricao = byId<HTMLInputElement>('locais-descricao');
    const inputSetor = byId<HTMLInputElement>('locais-setor');
    const selectCampus = byId<HTMLSelectElement>('locais-campus');
    const selectSede = byId<HTMLSelectElement>('locais-sede');
    const selectBloco = byId<HTMLSelectElement>('locais-bloco');
    const selectAndar = byId<HTMLSelectElement>('locais-andar');

    if (inputNome) inputNome.value = '';
    if (inputSigla) inputSigla.value = '';
    if (inputDescricao) inputDescricao.value = '';
    if (inputSetor) inputSetor.value = '';
    if (selectCampus) selectCampus.value = '';
    if (selectSede) selectSede.value = '';
    if (selectBloco) selectBloco.value = '';
    if (selectAndar) selectAndar.value = '';
  }

  private atualizarSelects() {
    const selectTipo = byId<HTMLSelectElement>('locais-tipo');
    const selectCampus = byId<HTMLSelectElement>('locais-campus');
    const selectSede = byId<HTMLSelectElement>('locais-sede');
    const selectBloco = byId<HTMLSelectElement>('locais-bloco');
    const selectAndar = byId<HTMLSelectElement>('locais-andar');

    if (!selectTipo) return;

    const tipo = selectTipo.value;

    // Campus
    if (selectCampus) {
      selectCampus.innerHTML = '<option value="">Selecione...</option>';
      this.campi.forEach(c => selectCampus.add(new Option(`${c.nome} (${c.sigla})`, c.sigla)));
    }

    // Sede (filtra por campus selecionado)
    if (selectSede) {
      selectSede.innerHTML = '<option value="">Selecione...</option>';
      if (selectCampus?.value) {
        const sedesFiltradas = this.sedes.filter(s => s.campus_sigla === selectCampus.value && s.ativo);
        sedesFiltradas.forEach(s => selectSede.add(new Option(s.nome, s.id)));
      }
    }

    // Bloco (filtra por sede selecionada)
    if (selectBloco) {
      selectBloco.innerHTML = '<option value="">Selecione...</option>';
      if (selectSede?.value) {
        const blocosFiltrados = this.blocos.filter(b => b.sede_id === selectSede.value && b.ativo);
        blocosFiltrados.forEach(b => selectBloco.add(new Option(b.nome, b.id)));
      }
    }

    // Andar (filtra por bloco selecionado)
    if (selectAndar) {
      selectAndar.innerHTML = '<option value="">Selecione...</option>';
      if (selectBloco?.value) {
        const andaresFiltrados = this.andares.filter(a => a.bloco_id === selectBloco.value && a.ativo);
        andaresFiltrados.forEach(a => selectAndar.add(new Option(a.nome, a.id)));
      }
    }

    this.toggleSelectsPorTipo(tipo);
  }

  private atualizarSelectSedes() {
    const selectCampus = byId<HTMLSelectElement>('locais-campus');
    const selectSede = byId<HTMLSelectElement>('locais-sede');

    if (!selectSede) return;

    selectSede.innerHTML = '<option value="">Selecione...</option>';
    if (selectCampus?.value) {
      const sedesFiltradas = this.sedes.filter(s => s.campus_sigla === selectCampus.value && s.ativo);
      sedesFiltradas.forEach(s => selectSede.add(new Option(s.nome, s.id)));
    }
  }

  private atualizarSelectBlocos() {
    const selectSede = byId<HTMLSelectElement>('locais-sede');
    const selectBloco = byId<HTMLSelectElement>('locais-bloco');

    if (!selectBloco) return;

    selectBloco.innerHTML = '<option value="">Selecione...</option>';
    if (selectSede?.value) {
      const blocosFiltrados = this.blocos.filter(b => b.sede_id === selectSede.value && b.ativo);
      blocosFiltrados.forEach(b => selectBloco.add(new Option(b.nome, b.id)));
    }
  }

  private atualizarSelectAndares() {
    const selectBloco = byId<HTMLSelectElement>('locais-bloco');
    const selectAndar = byId<HTMLSelectElement>('locais-andar');

    if (!selectAndar) return;

    selectAndar.innerHTML = '<option value="">Selecione...</option>';
    if (selectBloco?.value) {
      const andaresFiltrados = this.andares.filter(a => a.bloco_id === selectBloco.value && a.ativo);
      andaresFiltrados.forEach(a => selectAndar.add(new Option(a.nome, a.id)));
    }
  }

  private toggleSelectsPorTipo(tipo: string) {
    const campusWrapper = byId('locais-campus-wrapper');
    const sedeWrapper = byId('locais-sede-wrapper');
    const blocoWrapper = byId('locais-bloco-wrapper');
    const andarWrapper = byId('locais-andar-wrapper');
    const siglaWrapper = byId('locais-sigla-wrapper');
    const descricaoWrapper = byId('locais-descricao-wrapper');
    const setorWrapper = byId('locais-setor-wrapper');

    if (campusWrapper) campusWrapper.classList.toggle('hidden', tipo !== 'sede');
    if (sedeWrapper) sedeWrapper.classList.toggle('hidden', tipo !== 'bloco');
    if (blocoWrapper) blocoWrapper.classList.toggle('hidden', tipo !== 'andar');
    if (andarWrapper) andarWrapper.classList.toggle('hidden', tipo !== 'sala');
    if (siglaWrapper) siglaWrapper.classList.toggle('hidden', tipo !== 'sede' && tipo !== 'bloco');
    if (descricaoWrapper) descricaoWrapper.classList.toggle('hidden', tipo === 'sala');
    if (setorWrapper) setorWrapper.classList.toggle('hidden', tipo !== 'sala');
  }

  private async abrirModalEditar(tipo: string, id: string) {
    const modal = byId<HTMLDialogElement>('locais-modal');
    if (!modal) return;

    let item: Sede | Bloco | Andar | Sala | null = null;

    switch (tipo) {
      case 'sede':
        item = this.sedes.find(s => s.id === id) || null;
        break;
      case 'bloco':
        item = this.blocos.find(b => b.id === id) || null;
        break;
      case 'andar':
        item = this.andares.find(a => a.id === id) || null;
        break;
      case 'sala':
        item = this.salas.find(s => s.id === id) || null;
        break;
    }

    if (!item) return;

    const selectTipo = byId<HTMLSelectElement>('locais-tipo');
    const editId = byId<HTMLInputElement>('locais-edit-id');
    const editType = byId<HTMLInputElement>('locais-edit-type');
    const modalTitle = byId('locais-modal-title');
    const inputNome = byId<HTMLInputElement>('locais-nome');
    const inputSigla = byId<HTMLInputElement>('locais-sigla');
    const inputDescricao = byId<HTMLInputElement>('locais-descricao');
    const inputSetor = byId<HTMLInputElement>('locais-setor');
    const selectCampus = byId<HTMLSelectElement>('locais-campus');
    const selectSede = byId<HTMLSelectElement>('locais-sede');
    const selectBloco = byId<HTMLSelectElement>('locais-bloco');
    const selectAndar = byId<HTMLSelectElement>('locais-andar');

    if (selectTipo) {
      selectTipo.value = tipo;
      selectTipo.disabled = true;
    }
    if (editId) editId.value = id;
    if (editType) editType.value = tipo;
    if (modalTitle) modalTitle.textContent = 'Editar Item';
    if (inputNome) inputNome.value = item.nome;
    if (inputSigla) inputSigla.value = (item as Sede | Bloco).sigla || '';
    if (inputDescricao) inputDescricao.value = (item as Bloco | Andar | Sala).descricao || '';
    if (inputSetor) inputSetor.value = (item as Sala).setor || '';

    // Pré-selecionar os pais
    if (tipo === 'sede' && selectCampus) {
      selectCampus.value = (item as Sede).campus_sigla;
    } else if (tipo === 'bloco' && selectSede) {
      selectSede.value = (item as Bloco).sede_id;
    } else if (tipo === 'andar' && selectBloco) {
      selectBloco.value = (item as Andar).bloco_id;
    } else if (tipo === 'sala' && selectAndar) {
      selectAndar.value = (item as Sala).andar_id;
    }

    this.atualizarSelects();
    modal.showModal();
  }

  private async inativarItem(tipo: string, id: string) {
    const confirmado = await UIComponents.confirm(
      'Deseja realmente inativar este item? Todos os itens filhos também serão inativados.',
      'Confirmar Inativação'
    );

    if (!confirmado) return;

    try {
      switch (tipo) {
        case 'sede':
          await supabaseService.inativarSede(id);
          break;
        case 'bloco':
          await supabaseService.inativarBloco(id);
          break;
        case 'andar':
          await supabaseService.inativarAndar(id);
          break;
        case 'sala':
          await supabaseService.inativarSala(id);
          break;
      }

      UIComponents.success('Item inativado com sucesso!');
      await this.carregarDados();
      this.renderizarArvore();
    } catch (error) {
      console.error('Erro ao inativar:', error);
      UIComponents.error('Erro ao inativar item.');
    }
  }

  async salvar() {
    const selectTipo = byId<HTMLSelectElement>('locais-tipo');
    const editId = byId<HTMLInputElement>('locais-edit-id');
    const inputNome = byId<HTMLInputElement>('locais-nome');
    const inputSigla = byId<HTMLInputElement>('locais-sigla');
    const inputDescricao = byId<HTMLInputElement>('locais-descricao');
    const inputSetor = byId<HTMLInputElement>('locais-setor');
    const selectCampus = byId<HTMLSelectElement>('locais-campus');
    const selectSede = byId<HTMLSelectElement>('locais-sede');
    const selectBloco = byId<HTMLSelectElement>('locais-bloco');
    const selectAndar = byId<HTMLSelectElement>('locais-andar');

    if (!selectTipo || !inputNome) return;

    const tipo = selectTipo.value;
    const nome = inputNome.value.trim();
    const isEdit = !!editId?.value;

    if (!nome) {
      UIComponents.warning('Informe o nome.');
      return;
    }

    try {
      if (isEdit) {
        // Edição
        const id = editId!.value;
        switch (tipo) {
          case 'sede':
            await supabaseService.atualizarSede(id, { nome });
            break;
          case 'bloco':
            await supabaseService.atualizarBloco(id, { 
              nome, 
              sigla: inputSigla?.value.trim().toUpperCase() || '',
              descricao: inputDescricao?.value.trim() || null 
            });
            break;
          case 'andar':
            await supabaseService.atualizarAndar(id, { 
              nome, 
              descricao: inputDescricao?.value.trim() || null 
            });
            break;
          case 'sala':
            await supabaseService.atualizarSala(id, { 
              nome, 
              descricao: inputDescricao?.value.trim() || null,
              setor: inputSetor?.value.trim() || null 
            });
            break;
        }
      } else {
        // Criação
        switch (tipo) {
          case 'sede':
            if (!selectCampus?.value || !inputSigla?.value.trim()) {
              UIComponents.warning('Selecione o campus e informe a sigla.');
              return;
            }
            await supabaseService.criarSede({
              campus_sigla: selectCampus.value,
              nome,
              sigla: inputSigla.value.trim().toUpperCase(),
              ativo: true,
            });
            break;

          case 'bloco':
            if (!selectSede?.value || !inputSigla?.value.trim()) {
              UIComponents.warning('Selecione a sede e informe a sigla.');
              return;
            }
            await supabaseService.criarBloco({
              sede_id: selectSede.value,
              nome,
              sigla: inputSigla.value.trim().toUpperCase(),
              descricao: inputDescricao?.value.trim() || null,
              ativo: true,
            });
            break;

          case 'andar':
            if (!selectBloco?.value) {
              UIComponents.warning('Selecione o bloco.');
              return;
            }
            await supabaseService.criarAndar({
              bloco_id: selectBloco.value,
              nome,
              descricao: inputDescricao?.value.trim() || null,
              ativo: true,
            });
            break;

          case 'sala':
            if (!selectAndar?.value) {
              UIComponents.warning('Selecione o andar.');
              return;
            }
            await supabaseService.criarSala({
              andar_id: selectAndar.value,
              nome,
              descricao: inputDescricao?.value.trim() || null,
              setor: inputSetor?.value.trim() || null,
              ativo: true,
            });
            break;
        }
      }

      UIComponents.success(isEdit ? 'Item atualizado com sucesso!' : 'Item criado com sucesso!');
      this.fecharModal();
      await this.carregarDados();
      this.renderizarArvore();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      UIComponents.error('Erro ao salvar item.');
    }
  }

  private fecharModal() {
    const modal = byId<HTMLDialogElement>('locais-modal');
    if (modal) modal.close();
  }
}
